/* eslint-disable no-restricted-imports */
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import {
	ArrowBackIcon,
	ArrowIcon,
	ITheme,
	StyledIconLinkButton as IconLinkButton,
} from '../../../../ui';
import React from 'react';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { Color } from '../../../shared/styles/Color';
import { removePaginationFromPath } from '../../helpers/removePaginationFromPath';
import Responsive from '../../../../ui/utils/Responsive';
import themed from '../../../../ui/themer/themed';

export interface IPageNavigation {
	pageNumber: number;
	numberOfPages: number;
	onPageChange?: () => void;
}

export const PageNavigation = ({
	pageNumber,
	numberOfPages,
	onPageChange,
}: IPageNavigation) => {
	const { t } = useTranslation();
	const router = useRouter();

	const generatePageLink = (newPage: number) => {
		const asPath = removePaginationFromPath(router.asPath);
		return `${asPath}/${newPage}`;
	};

	const backButtonDisabled = pageNumber === 1;
	const nextButtonDisabled = pageNumber === numberOfPages;

	return (
		<StyledContainer>
			{backButtonDisabled ? (
				<StyledIconButton disabled aria-disabled>
					<ArrowBackIcon variation="inverted" />
				</StyledIconButton>
			) : (
				<StyledIconLinkButton
					href={generatePageLink(pageNumber - 1)}
					aria-label={t('previous_page')}
					onClick={onPageChange}
				>
					<ArrowBackIcon variation="inverted" />
				</StyledIconLinkButton>
			)}

			<StyledLabel>
				{t('pagination_label', { pageNumber, numberOfPages })}
			</StyledLabel>
			{nextButtonDisabled ? (
				<StyledIconButton disabled aria-disabled>
					<ArrowIcon variation="inverted" />
				</StyledIconButton>
			) : (
				<StyledIconLinkButton
					href={generatePageLink(pageNumber + 1)}
					aria-label={t('next_page')}
					onClick={onPageChange}
				>
					<ArrowIcon variation="inverted" />
				</StyledIconLinkButton>
			)}
		</StyledContainer>
	);
};

const StyledContainer = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;
	gap: 40px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		justify-content: space-between;
	}
`;

const StyledLabel = styled.span<{}, ITheme>`
	text-transform: uppercase;
	font-size: 12px;
	letter-spacing: 1px;
	line-height: 14.4px;
	color: ${Color.TextGrey};
`;

const StyledIconLinkButton = styled(IconLinkButton)`
	width: fit-content;
	height: auto;
	padding: 8px;
	box-shadow: none;

	&:active {
		transform: ${themed(({ search }) => search.button.active.transform)};
		box-shadow: none !important;
	}

	svg {
		margin: 0;
		width: 24px;
		height: 24px;
	}
`;

const StyledIconButton = styled(IconLinkButton.withComponent('button'))`
	width: fit-content;
	height: auto;
	padding: 8px;
	box-shadow: none;
	pointer-events: none;
	background-color: ${Color.Neutral90} !important;
	border-color: ${Color.Neutral90} !important;
	color: ${Color.TextWhite};

	svg {
		margin: 0;
		width: 24px;
		height: 24px;
	}
`;
